// Copyright ©️ 2026 eVolve MEP, LLC

import type { DOMAttributes, ReactNode } from 'react';

import { Card, Flex, Text, Title, type CardSectionProps, type TextProps, useMantineTheme } from '@mantine/core';

import { InfoIcon } from 'components/Mantine/InfoIcon';

type BendDataOutputProps = {
  /**
   * The label for this output. Rendered inside a {@link Title} component.
   * @remarks This renders as a heading element, so be aware of DOM restrictions.
   **/
  label: string;
  tooltip?: ReactNode;
  /**
   * The value for this output. Rendered inside a {@link Text} component.
   * @remarks This renders as a `p` element, so be aware of DOM restrictions.
   **/
  value: ReactNode;
  /** Props passed to the Card.Section component, used as a container for this entire component */
  cardSectionProps?: CardSectionProps & DOMAttributes<HTMLDivElement>;
  /** Props passed to the Text component used to render the value text */
  textProps?: TextProps;
  /** When provided, highlights the title. */
  highlighted?: boolean;
};

/** A card section component used to display bend data values. */
export const BendDataOutput = ({
  label,
  tooltip,
  value,
  cardSectionProps,
  textProps,
  highlighted,
}: BendDataOutputProps) => {
  const theme = useMantineTheme();

  return (
    <Card.Section px="xs" py={2} {...cardSectionProps}>
      <Flex align="center" gap="xs">
        <Title order={6} c={highlighted ? theme.primaryColor : undefined}>
          {label}
        </Title>
        <InfoIcon label={tooltip} />
      </Flex>
      <Text {...textProps}>{value}</Text>
    </Card.Section>
  );
};
